import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../api/client'
import { useAuthStore } from '../../stores/auth'
import { Button, Card, Input } from '../ui'

/** "Edit profile": own display names (PATCH /me). Email/username are the
 *  login identity and immutable — shown read-only for context. The save
 *  writes the returned principal into the auth store, so the sidebar
 *  "this is you" chip updates immediately. */
export function EditProfileCard() {
  const principal = useAuthStore((s) => s.principal)
  const setPrincipal = useAuthStore((s) => s.setPrincipal)
  const queryClient = useQueryClient()

  const [firstName, setFirstName] = useState(principal?.first_name ?? '')
  const [lastName, setLastName] = useState(principal?.last_name ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  // The principal may arrive after first render (session restore) — resync
  // until the user has typed something of their own.
  const [dirty, setDirty] = useState(false)
  useEffect(() => {
    if (!dirty) {
      setFirstName(principal?.first_name ?? '')
      setLastName(principal?.last_name ?? '')
    }
  }, [principal, dirty])

  async function save(e: React.FormEvent) {
    e.preventDefault()
    if (busy) return
    setBusy(true)
    setError(null)
    setSaved(false)
    try {
      const updated = await api.patchMe({ first_name: firstName.trim(), last_name: lastName.trim() })
      setPrincipal(updated)
      await queryClient.invalidateQueries({ queryKey: ['profile'] })
      setDirty(false)
      setSaved(true)
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : 'Could not save your profile')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title="Edit profile">
      <form className="flex flex-col gap-3" onSubmit={(e) => void save(e)}>
        <div className="grid gap-3 sm:grid-cols-2">
          <Input
            label="First name"
            autoComplete="given-name"
            value={firstName}
            onChange={(e) => {
              setFirstName(e.target.value)
              setDirty(true)
              setSaved(false)
            }}
          />
          <Input
            label="Last name"
            autoComplete="family-name"
            value={lastName}
            onChange={(e) => {
              setLastName(e.target.value)
              setDirty(true)
              setSaved(false)
            }}
          />
        </div>
        <p className="text-[11px] leading-relaxed text-ostrel-ink-muted">
          Your email (<span className="tnum font-mono">{principal?.email || principal?.username}</span>) is your
          sign-in identity and cannot be changed here.
        </p>
        {error && <p className="text-xs text-ostrel-danger">{error}</p>}
        {saved && <p className="text-xs text-ostrel-ok">Profile saved.</p>}
        <div className="flex justify-end">
          <Button type="submit" variant="navy" size="sm" disabled={busy}>
            {busy ? 'Saving…' : 'Save profile'}
          </Button>
        </div>
      </form>
    </Card>
  )
}
